import {
  FormControl,
  FormLabel,
  Input,
  Select,
  FormErrorMessage,
} from '@chakra-ui/react'
import BasicDateStringPicker from '../../../../../../components/BasicDateStringPicker'

interface OccupationFormProps {
  occupationName: string
  setOccupationName: (value: string) => void
  startDate: string
  setStartDate: (value: string) => void
  endDate: string
  setEndDate: (value: string) => void
  stressLevel: string
  setStressLevel: (value: string) => void
  extraInfoRequired: boolean
}

export default function OccupationForm({
  occupationName,
  setOccupationName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  stressLevel,
  setStressLevel,
  extraInfoRequired,
}: OccupationFormProps) {
  // Helper to check if a date from BasicDateStringPicker is empty/incomplete
  // BasicDateStringPicker returns values like "0-0-0", "2021-0-0", "2021-10-0" when incomplete
  const isDateEmpty = (date: string) => {
    if (!date.trim()) return true
    // Check for incomplete dates: starts with "0-", contains "-0-", or ends with "-0"
    return date.startsWith('0-') || date.includes('-0-') || date.endsWith('-0')
  }

  // Helper function to check if end date is after start date
  const isEndDateAfterStartDate = () => {
    if (isDateEmpty(endDate) || isDateEmpty(startDate)) return true
    return new Date(endDate) > new Date(startDate)
  }

  const occupationNameError = extraInfoRequired && !occupationName.trim()
  const startDateError = extraInfoRequired && isDateEmpty(startDate)
  const endDateError = !isDateEmpty(endDate) && !isEndDateAfterStartDate()
  const stressLevelError = extraInfoRequired && !stressLevel.trim()

  return (
    <>
      <FormControl isRequired isInvalid={occupationNameError}>
        <FormLabel>Occupation Name</FormLabel>
        <Input
          id='occupationName'
          placeholder='Enter occupation name'
          value={occupationName}
          onChange={(e) => setOccupationName(e.target.value)}
        />
        {occupationNameError && (
          <FormErrorMessage>Occupation name is required</FormErrorMessage>
        )}
      </FormControl>

      <FormControl mt={4} isRequired isInvalid={startDateError}>
        <FormLabel>Start Date</FormLabel>
        <BasicDateStringPicker
          startYear='1930'
          endYear={new Date().getFullYear().toString()}
          dateString={startDate}
          setDateString={setStartDate}
        />
        {startDateError && (
          <FormErrorMessage>Start date is required</FormErrorMessage>
        )}
      </FormControl>

      <FormControl mt={4} isInvalid={endDateError}>
        <FormLabel>End Date (leave blank if current)</FormLabel>
        <BasicDateStringPicker
          startYear='1930'
          endYear={new Date().getFullYear().toString()}
          dateString={endDate}
          setDateString={setEndDate}
        />
        {endDateError && (
          <FormErrorMessage>End date must be after start date</FormErrorMessage>
        )}
      </FormControl>

      <FormControl mt={4} isRequired isInvalid={stressLevelError}>
        <FormLabel>Stress Level</FormLabel>
        <Select
          id='stressLevel'
          placeholder='Select stress level'
          value={stressLevel}
          onChange={(e) => setStressLevel(e.target.value)}
        >
          <option value='High Stress'>High Stress</option>
          <option value='Medium High Stress'>Medium High Stress</option>
          <option value='Medium Stress'>Medium Stress</option>
          <option value='Medium Low Stress'>Medium Low Stress</option>
          <option value='Low Stress'>Low Stress</option>
        </Select>
        {stressLevelError && (
          <FormErrorMessage>Please select a stress level</FormErrorMessage>
        )}
      </FormControl>
    </>
  )
}
